import { useEffect, useState, useMemo } from "react";
import { Star, BadgeCheck, X, ChevronLeft, ChevronRight, Award } from "lucide-react";
import { getProductReviews, getReviewsPolicy } from "@/lib/api";
import { Dialog, DialogContent } from "@/components/ui/dialog";

const scrollToReviews = () => {
  const el = document.getElementById("reviews");
  if (el) el.scrollIntoView({ behavior: "smooth" });
};

function StarRow({ n, size = "h-4 w-4" }) {
  const val = Number(n) || 0;
  return (
    <span className="inline-flex">
      {[1, 2, 3, 4, 5].map((i) => {
        const full = i <= Math.floor(val);
        const half = !full && val >= i - 0.5;
        return (
          <span key={i} className={`relative inline-block ${size}`}>
            <Star className={`${size} absolute inset-0 text-muted-foreground/40`} />
            {(full || half) && (
              <span className={`absolute inset-0 overflow-hidden ${half ? "w-1/2" : "w-full"}`}>
                <Star className={`${size} fill-lime text-lime`} />
              </span>
            )}
          </span>
        );
      })}
    </span>
  );
}

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString("it-IT", { day: "2-digit", month: "long", year: "numeric" }) : "");

// Badge compatto sotto al nome prodotto
export function ProductRating({ productId, data }) {
  const summary = data?.summary;
  if (!summary || summary.count === 0) return null;
  return (
    <button onClick={scrollToReviews} className="mt-3 inline-flex items-center gap-2 text-sm" data-testid="pdp-rating-badge">
      <StarRow n={Math.round(summary.avg * 2) / 2} />
      <span className="font-semibold text-foreground">{String(summary.avg).replace(".", ",")}</span>
      <span className="text-muted-foreground underline-offset-2 hover:underline">· {summary.count} recensioni</span>
    </button>
  );
}

function Lightbox({ photos, index, onClose, onNav, review }) {
  if (index === null) return null;
  const photo = photos[index];
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-3xl border-border bg-card p-0" data-testid="review-lightbox">
        <div className="relative">
          <img src={photo.url} alt="" className="max-h-[60vh] w-full object-contain bg-black" />
          {photos.length > 1 && (
            <>
              <button onClick={() => onNav(-1)} className="absolute left-2 top-1/2 -translate-y-1/2 rounded-full bg-black/50 p-2 text-white" aria-label="Precedente"><ChevronLeft className="h-5 w-5" /></button>
              <button onClick={() => onNav(1)} className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full bg-black/50 p-2 text-white" aria-label="Successiva"><ChevronRight className="h-5 w-5" /></button>
            </>
          )}
          <button onClick={onClose} className="absolute right-2 top-2 rounded-full bg-black/50 p-2 text-white" aria-label="Chiudi"><X className="h-4 w-4" /></button>
        </div>
        {review && (
          <div className="p-5">
            <StarRow n={review.stars} />
            {review.title && <p className="mt-2 font-display text-lg font-bold text-foreground">{review.title}</p>}
            <p className="mt-1 text-sm text-muted-foreground">{review.text}</p>
            <p className="mt-2 text-xs text-muted-foreground">{review.display_name}{review.city ? ` · ${review.city}` : ""}</p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export function ProductReviewsSection({ productId, data }) {
  const [policy, setPolicy] = useState("");
  const [showPolicy, setShowPolicy] = useState(false);
  const [filter, setFilter] = useState("all"); // all | photos | 1..5
  const [sort, setSort] = useState("recent");
  const [limit, setLimit] = useState(5);
  const [lb, setLb] = useState({ reviewId: null, index: null });

  useEffect(() => { getReviewsPolicy().then((p) => setPolicy(p.text)).catch(() => {}); }, []);

  const summary = data?.summary;
  const reviews = data?.reviews || [];

  const filtered = useMemo(() => {
    let list = [...reviews];
    if (filter === "photos") list = list.filter((r) => (r.photos || []).length > 0);
    else if (filter !== "all") list = list.filter((r) => r.stars === Number(filter));
    if (sort === "recent") list.sort((a, b) => new Date(b.date) - new Date(a.date));
    else if (sort === "high") list.sort((a, b) => b.stars - a.stars);
    else if (sort === "low") list.sort((a, b) => a.stars - b.stars);
    return list;
  }, [reviews, filter, sort]);

  if (!summary || summary.count === 0) return null;

  const maxDist = Math.max(...Object.values(summary.distribution));
  const activeReview = reviews.find((r) => r.id === lb.reviewId);
  const openLb = (review, i) => setLb({ reviewId: review.id, index: i });
  const navLb = (dir) => {
    const photos = activeReview?.photos || [];
    setLb((s) => ({ ...s, index: (s.index + dir + photos.length) % photos.length }));
  };

  return (
    <section id="reviews" className="mt-20 border-t border-border pt-14" data-testid="pdp-reviews-section">
      <h2 className="font-display text-2xl font-extrabold tracking-tight text-foreground sm:text-3xl">Recensioni</h2>

      <div className="mt-6 grid gap-8 lg:grid-cols-[280px_1fr]">
        {/* Riepilogo */}
        <div>
          <div className="flex items-end gap-3">
            <span className="font-display text-5xl font-extrabold text-foreground">{String(summary.avg).replace(".", ",")}</span>
            <div className="pb-1"><StarRow n={Math.round(summary.avg * 2) / 2} /><p className="mt-1 text-sm text-muted-foreground">{summary.count} recensioni</p></div>
          </div>
          <div className="mt-4 flex flex-col gap-1.5">
            {[5, 4, 3, 2, 1].map((n) => (
              <button key={n} onClick={() => setFilter(filter === String(n) ? "all" : String(n))} className="flex items-center gap-2 text-sm" data-testid={`review-dist-${n}`}>
                <span className="w-3 text-muted-foreground">{n}</span>
                <Star className="h-3 w-3 fill-lime text-lime" />
                <span className="h-2 flex-1 overflow-hidden rounded-full bg-surface3">
                  <span className="block h-full bg-lime" style={{ width: `${maxDist ? (summary.distribution[n] / maxDist) * 100 : 0}%` }} />
                </span>
                <span className="w-6 text-right text-muted-foreground">{summary.distribution[n]}</span>
              </button>
            ))}
          </div>
          {policy && (
            <button onClick={() => setShowPolicy(true)} className="mt-4 text-xs text-muted-foreground underline underline-offset-2" data-testid="reviews-policy-link">
              Come verifichiamo le recensioni
            </button>
          )}
        </div>

        {/* Elenco */}
        <div>
          {/* Foto dei clienti */}
          {summary.photos.length > 0 && (
            <div className="mb-6">
              <p className="mb-2 text-sm font-semibold text-foreground">Foto dei clienti</p>
              <div className="flex gap-2 overflow-x-auto pb-1" data-testid="reviews-customer-photos">
                {summary.photos.map((ph, i) => {
                  const rev = reviews.find((r) => r.id === ph.review_id);
                  const idx = rev ? (rev.photos || []).findIndex((x) => x.url === ph.url) : 0;
                  return <img key={i} src={ph.url} alt="" onClick={() => rev && openLb(rev, Math.max(0, idx))} className="h-20 w-20 shrink-0 cursor-pointer rounded-xl border border-border object-cover" />;
                })}
              </div>
            </div>
          )}

          {/* Filtri + ordinamento */}
          <div className="mb-4 flex flex-wrap items-center gap-2">
            <button onClick={() => setFilter("all")} className={`rounded-full px-3 py-1 text-sm ${filter === "all" ? "bg-lime text-ink" : "bg-card text-muted-foreground"}`}>Tutte</button>
            <button onClick={() => setFilter("photos")} className={`rounded-full px-3 py-1 text-sm ${filter === "photos" ? "bg-lime text-ink" : "bg-card text-muted-foreground"}`}>Con foto</button>
            <select value={sort} onChange={(e) => setSort(e.target.value)} className="ml-auto rounded-full border border-border bg-card px-3 py-1 text-sm text-foreground" data-testid="reviews-sort">
              <option value="recent">Più recenti</option>
              <option value="high">Voto più alto</option>
              <option value="low">Voto più basso</option>
            </select>
          </div>

          <div className="flex flex-col gap-6" data-testid="reviews-list">
            {filtered.slice(0, limit).map((r) => (
              <article key={r.id} className="border-b border-border pb-6 last:border-0">
                <div className="flex items-center justify-between gap-2">
                  <StarRow n={r.stars} />
                  <div className="flex items-center gap-2">
                    {r.premium && (
                      <span className="inline-flex items-center gap-1 rounded-full bg-carrot/10 px-2 py-0.5 text-[11px] font-semibold text-carrot" data-testid="review-premium-badge">
                        <Award className="h-3.5 w-3.5" /> Cliente Top
                      </span>
                    )}
                    {r.verified && <span className="inline-flex items-center gap-1 text-xs font-medium text-lime"><BadgeCheck className="h-4 w-4" /> Acquisto verificato</span>}
                  </div>
                </div>
                {r.title && <h3 className="mt-2 font-display text-base font-bold text-foreground">{r.title}</h3>}
                <p className="mt-1 text-sm leading-relaxed text-muted-foreground">{r.text}</p>
                <p className="mt-2 text-xs text-muted-foreground">
                  {r.display_name}{r.city ? ` · ${r.city}` : ""} · {fmtDate(r.date)}{r.variant_label ? ` · ${r.variant_label}` : ""}
                </p>
                {(r.photos || []).length > 0 && (
                  <div className="mt-3 flex gap-2">
                    {r.photos.map((ph, i) => <img key={ph.id || i} src={ph.url} alt="" onClick={() => openLb(r, i)} className="h-16 w-16 cursor-pointer rounded-lg border border-border object-cover" />)}
                  </div>
                )}
                {r.shop_reply && (
                  <div className="mt-3 rounded-xl bg-card p-3">
                    <p className="text-xs font-semibold text-foreground">Risposta di RINOVA</p>
                    <p className="mt-1 text-sm text-muted-foreground">{r.shop_reply}</p>
                  </div>
                )}
              </article>
            ))}
          </div>

          {filtered.length > limit && (
            <button onClick={() => setLimit((l) => l + 5)} className="mt-6 rounded-full border border-border px-5 py-2.5 text-sm font-semibold text-foreground" data-testid="reviews-show-more">
              Mostra altre
            </button>
          )}
        </div>
      </div>

      <Lightbox photos={activeReview?.photos || []} index={lb.index} review={activeReview} onClose={() => setLb({ reviewId: null, index: null })} onNav={navLb} />

      <Dialog open={showPolicy} onOpenChange={setShowPolicy}>
        <DialogContent className="border-border bg-card">
          <h3 className="font-display text-lg font-bold text-foreground">Come verifichiamo le recensioni</h3>
          <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{policy}</p>
        </DialogContent>
      </Dialog>
    </section>
  );
}

// JSON-LD schema.org con AggregateRating e Review (solo recensioni pubblicate)
export function ReviewsJsonLd({ productName, data }) {
  if (!data?.summary || data.summary.count === 0) return null;
  const json = {
    "@context": "https://schema.org",
    "@type": "Product",
    name: productName,
    aggregateRating: {
      "@type": "AggregateRating",
      ratingValue: data.summary.avg,
      reviewCount: data.summary.count,
    },
    review: (data.reviews || []).slice(0, 20).map((r) => ({
      "@type": "Review",
      reviewRating: { "@type": "Rating", ratingValue: r.stars, bestRating: 5 },
      author: { "@type": "Person", name: r.display_name },
      datePublished: r.date,
      name: r.title,
      reviewBody: r.text,
    })),
  };
  return <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(json) }} />;
}
